import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "q-py-sm" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 0,
  class: "animated-ellipsis"
}

import { useDialogPluginComponent } from "quasar"
import SfxBtn from "src/components/button/SfxBtn.vue"
import SfxBtnOutline from "src/components/button/SfxBtnOutline.vue"
import { AppBaseDB, AppCommonDB, getCommonDB, getDB } from "src/db/databases"
import { useApiAbortStore } from "src/stores/apiAbortStore"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import {
  fetchAttributeCharListData,
  fetchAttributeNumericListData,
  fetchAttributeSelectListData,
  fetchAttributeSelectOptionsListData,
  fetchAttributeTemporalListData,
  fetchCorrectiveWorkOnItemListData,
  fetchIdentifierListData,
  fetchIssueOnItemActionsListData,
  fetchIssueOnItemCommentListData,
  fetchIssueOnItemListData,
  fetchIssuePriorityListData,
  fetchItemAttributeCharValuesListData,
  fetchItemAttributeNumericValuesListData,
  fetchItemAttributeSelectValuesListData,
  fetchItemAttributeTemporalValuesListData,
  fetchItemClassListData,
  fetchItemListData,
  fetchRegularWorkOnItemListData,
  fetchUsersForWorkspaceListData,
  fetchWorkDomainWorkspaceListData,
  fetchWorkOrderCorrectiveItemCommentListData,
  fetchWorkOrderCorrectiveItemListData,
  fetchWorkOrderPriorityListData,
  fetchWorkOrderRegularItemCommentListData,
  fetchWorkOrderRegularItemListData,
} from "src/util/functions/entityData"
import { showSuccessNotification } from "src/util/functions/notifications"
import { getNewAbortHandler } from "src/util/http/httpHelper"
import { HttpAbortConfig, HttpConfig } from "src/util/types/dataModels/http"
import { computed, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxDialogDataFetch',
  props: {
  workspaceId: {
    type: String,
    required: true,
  },
},
  emits: [...useDialogPluginComponent.emits],
  setup(__props) {

const props = __props



//endregion

//region Variables

const { dialogRef, onDialogOK } = useDialogPluginComponent()
const { t } = useI18n()

const title = ref(t("sfxDialogDataFetch.title"))
const explanation = t("sfxDialogDataFetch.explanation")
const btnCancelLabel = t("sfxButtons.btn_cancel")
const btnOkLabel = t("sfxButtons.btn_ok")

const itemClassFinished = ref<boolean | null | undefined>(undefined)
const attributesFinished = ref<boolean | null | undefined>(undefined)
const itemListFinished = ref<boolean | null | undefined>(undefined)
const itemAttributeValuesFinished = ref<boolean | null | undefined>(undefined)
const issuesFinished = ref<boolean | null | undefined>(undefined)
const workFinished = ref<boolean | null | undefined>(undefined)
const commentsFinished = ref<boolean | null | undefined>(undefined)
const identifiersFinished = ref<boolean | null | undefined>(undefined)
const usersFinished = ref<boolean | null | undefined>(undefined)
const allLoaded = ref(false)
const abortHandler: HttpAbortConfig = getNewAbortHandler("GET")

const errorInFetches = computed(() =>
  [
    itemClassFinished.value,
    attributesFinished.value,
    itemListFinished.value,
    itemAttributeValuesFinished.value,
    issuesFinished.value,
    workFinished.value,
    commentsFinished.value,
    identifiersFinished.value,
    usersFinished.value,
  ].includes(null)
)

const dataFetchList = computed<[string, boolean | null | undefined][]>(() => [
  [t("sfxDialogDataFetch.item_class"), itemClassFinished.value],
  [t("sfxDialogDataFetch.attributes"), attributesFinished.value],
  [t("sfxDialogDataFetch.items"), itemListFinished.value],
  [t("sfxDialogDataFetch.item_attributes"), itemAttributeValuesFinished.value],
  [t("sfxDialogDataFetch.issues"), issuesFinished.value],
  [t("sfxDialogDataFetch.work"), workFinished.value],
  [t("sfxDialogDataFetch.comments"), commentsFinished.value],
  [t("sfxDialogDataFetch.identifiers"), identifiersFinished.value],
  [t("sfxDialogDataFetch.users"), usersFinished.value],
])

//endregion

//region Lifecycle

onMounted(async () => {
  try {
    abortHandler.batchRequest = true
    const config: HttpConfig = { abortHandler }
    const db = await getDB()
    const commonDb = await getCommonDB()

    await Promise.all([
      itemClassData(config, db),
      attributesData(config, db),
      itemData(config, db),
      itemAttributeData(config, db),
      issuesData(config, db),
      workData(config, db),
      commentData(config, db),
      identifierData(config, db),
      userData(config, commonDb),
    ])

    allLoaded.value = true
  } catch {}
})

//endregion

//region Functions

async function itemClassData(config: HttpConfig, db: AppBaseDB) {
  try {
    itemClassFinished.value = await fetchItemClassListData(config, db)
  } catch {
    itemClassFinished.value = null
  }
}

async function attributesData(config: HttpConfig, db: AppBaseDB) {
  try {
    const attributesTotal = await Promise.all([
      fetchAttributeCharListData(config, db),
      fetchAttributeNumericListData(config, db),
      fetchAttributeSelectListData(config, db),
      fetchAttributeSelectOptionsListData(config, db),
      fetchAttributeTemporalListData(config, db),
    ])

    attributesFinished.value = attributesTotal.every(Boolean)
  } catch {
    attributesFinished.value = null
  }
}

async function itemData(config: HttpConfig, db: AppBaseDB) {
  try {
    itemListFinished.value = await fetchItemListData(config, db)
  } catch {
    itemListFinished.value = null
  }
}

async function itemAttributeData(config: HttpConfig, db: AppBaseDB) {
  try {
    const itemAttributeValuesTotal = await Promise.all([
      fetchItemAttributeCharValuesListData(config, db),
      fetchItemAttributeNumericValuesListData(config, db),
      fetchItemAttributeSelectValuesListData(config, db),
      fetchItemAttributeTemporalValuesListData(config, db),
    ])

    itemAttributeValuesFinished.value = itemAttributeValuesTotal.every(Boolean)
  } catch {
    itemAttributeValuesFinished.value = null
  }
}

async function issuesData(config: HttpConfig, db: AppBaseDB) {
  try {
    const issueAll = await Promise.all([
      fetchIssuePriorityListData(config, db),
      fetchIssueOnItemListData(config, db),
      fetchIssueOnItemActionsListData(config, db),
    ])

    issuesFinished.value = issueAll.every(Boolean)
  } catch {
    issuesFinished.value = null
  }
}

async function workData(config: HttpConfig, db: AppBaseDB) {
  try {
    const workTotal = await Promise.all([
      fetchWorkOrderPriorityListData(config, db),
      fetchCorrectiveWorkOnItemListData(config, db),
      fetchRegularWorkOnItemListData(config, db),
      fetchWorkDomainWorkspaceListData(config, db),
      fetchWorkOrderCorrectiveItemListData(config, db),
      fetchWorkOrderRegularItemListData(config, db),
    ])

    workFinished.value = workTotal.every(Boolean)
  } catch {
    workFinished.value = null
  }
}

async function commentData(config: HttpConfig, db: AppBaseDB) {
  try {
    const commentTotal = await Promise.all([
      fetchIssueOnItemCommentListData(config, db),
      fetchWorkOrderCorrectiveItemCommentListData(config, db),
      fetchWorkOrderRegularItemCommentListData(config, db),
    ])

    commentsFinished.value = commentTotal.every(Boolean)
  } catch {
    commentsFinished.value = null
  }
}

async function identifierData(config: HttpConfig, db: AppBaseDB) {
  try {
    identifiersFinished.value = await fetchIdentifierListData(config, db)
  } catch {
    identifiersFinished.value = null
  }
}

async function userData(config: HttpConfig, db: AppCommonDB) {
  try {
    usersFinished.value = await fetchUsersForWorkspaceListData(props.workspaceId, config, db)
  } catch {
    usersFinished.value = null
  }
}

function handleCancel() {
  unwatchDataLoad()
  abortHandler.controller.abort()
  const apiAbortStore = useApiAbortStore()
  apiAbortStore.removeController(abortHandler.id)
  onDialogOK(false)
}

function handleOk() {
  onDialogOK(true)
}

//endregion

//region Setup Code

const unwatchDataLoad = watch(
  () => allLoaded.value,
  (dataLoaded) => {
    if (dataLoaded && !errorInFetches.value) {
      showSuccessNotification(t("notifications.data_fetched"))
      handleOk()
    }
  }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    persistent: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_card_section, { class: "q-dialog__title text-h3 flex justify-between" }, {
            default: _withCtx(() => [
              _createElementVNode("span", null, _toDisplayString(title.value), 1),
              _createVNode(_component_q_icon, {
                name: _unref(Icons).DOWNLOAD
              }, null, 8, ["name"])
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, { class: "text-body1" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dataFetchList.value, (value, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: index,
                    class: "row items-center justify-between"
                  }, [
                    _createElementVNode("div", null, [
                      _createElementVNode("span", {
                        innerHTML: value[0]
                      }, null, 8, _hoisted_2),
                      (value[1] === undefined)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_3))
                        : _createCommentVNode("", true)
                    ]),
                    (value[1] === null)
                      ? (_openBlock(), _createBlock(_component_q_icon, {
                          key: 0,
                          name: _unref(Icons).REJECT,
                          color: "negative",
                          class: "text-h3"
                        }, null, 8, ["name"]))
                      : (value[1] !== undefined)
                        ? (_openBlock(), _createBlock(_component_q_icon, {
                            key: 1,
                            name: _unref(Icons).ACCEPT,
                            color: "positive",
                            class: "text-h3"
                          }, null, 8, ["name"]))
                        : _createCommentVNode("", true)
                  ]))
                }), 128))
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, { class: "text-body1 text-italic text-inactive" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(explanation)), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_actions, { class: "q-px-md q-pb-md" }, {
            default: _withCtx(() => [
              (!allLoaded.value)
                ? (_openBlock(), _createBlock(SfxBtnOutline, {
                    key: 0,
                    onClick: handleCancel,
                    label: _unref(btnCancelLabel),
                    class: "col"
                  }, null, 8, ["label"]))
                : (_openBlock(), _createBlock(SfxBtn, {
                    key: 1,
                    onClick: handleOk,
                    label: _unref(btnOkLabel),
                    class: "col"
                  }, null, 8, ["label"]))
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 512))
}
}

})